const API_BASE = 'https://api.dev.auxo.dk/public';

/**
 * Henter et enkelt element via dets slug og sprogkode.
 * @param {string} slug - Elementets slug (f.eks. 'om-os')
 * @param {string} [lang='da'] - Sprogkoden
 */
export async function fetchBySlug(slug, lang = 'da') {
  try {
    const response = await fetch(`${API_BASE}/by-slug/${lang}/${slug}`);
    if (!response.ok) {
      throw new Error(`Kunne ikke hente element med slug: ${slug}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Fejl i fetchBySlug:', error);
    return null;
  }
}

/**
 * Henter alle elementer i en bestemt samling som et simpelt array.
 * @param {string} collectionName - Samlingens navn (f.eks. 'sider', 'dahliaknolde')
 */
export async function fetchCollection(collectionName) {
  try {
    const response = await fetch(`${API_BASE}/content/${collectionName}`);
    if (!response.ok) {
      throw new Error(`Kunne ikke hente samling: ${collectionName}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Fejl i fetchCollection for ${collectionName}:`, error);
    return [];
  }
}

/**
 * Henter alle sider fra samlingen 'sider'.
 */
export async function fetchPages() {
  return fetchCollection('sider');
}

/**
 * Henter en specifik side via slug fra 'sider'.
 */
export async function fetchPageBySlug(slug) {
  return fetchBySlug(slug, 'da');
}

/**
 * Henter alle dahliaknolde fra samlingen 'dahliaknolde'.
 * Henter op til 250 elementer for at dække det fulde sortiment.
 */
export async function fetchDahliaBulbs() {
  try {
    const response = await fetch(`${API_BASE}/v2/content/dahliaknolde?limit=250`);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data.items) && data.items.length > 0) {
        return data.items;
      }
    }
  } catch (err) {
    console.warn('Kunne ikke hente v2 dahliaknolde, prøver v1:', err);
  }
  return fetchCollection('dahliaknolde');
}

/**
 * Henter alle arrangementer fra samlingen 'arrangementer'.
 */
export async function fetchEvents() {
  return fetchCollection('arrangementer');
}

/**
 * Henter alle galleriposter fra samlingen 'galleri'.
 */
export async function fetchGalleri() {
  return fetchCollection('galleri');
}

/**
 * Henter et galleri via dets slug fra 'galleri'.
 */
export async function fetchGalleryBySlug(slug) {
  return fetchBySlug(slug, 'da');
}

/**
 * Henter billeder fra den dedikerede samling 'bordpynt_galleri'.
 */
export async function fetchBordpyntGalleri() {
  try {
    const response = await fetch(`${API_BASE}/v2/content/bordpynt_galleri?sort=sortering&order=asc&limit=100`);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data.items)) {
        return data.items;
      }
    }
  } catch (err) {
    console.warn('Kunne ikke hente v2 bordpynt_galleri, prøver v1:', err);
  }
  return fetchCollection('bordpynt_galleri');
}

/**
 * Henter forside-slides fra samlingen 'forside_slides'.
 */
export async function fetchForsideSlides() {
  return fetchCollection('forside_slides');
}

/**
 * Henter kundeanmeldelser fra samlingen 'anmeldelser'.
 */
export async function fetchAnmeldelser() {
  return fetchCollection('anmeldelser');
}

/**
 * Henter forside-sektioner / fremhævede kasser fra 'forside_sektioner'.
 */
export async function fetchForsideSektioner() {
  return fetchCollection('forside_sektioner');
}

/**
 * Henter virksomhedens generelle kontakt- og åbningsinformationer.
 */
export async function fetchKontaktInformation() {
  try {
    const data = await fetchCollection('kontaktinformation');
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch (err) {
    console.error('Fejl ved hentning af kontaktinformation:', err);
    return null;
  }
}
